import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Media } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { availableCompanies, selectCompany } from '../../selectors/company';
import * as dashboardActions from '../../store/dashboard/actions';
import * as companiesActions from '../../store/companies/actions';
import colors from '../CommonStyle/colors';

// Lets the header give this selector only the room its icons leave, so a long
// company name is truncated on phones instead of pushing the icons off screen
const shrinkableStyle = { minWidth: 0 };

const CompanySelector = ({ companyId }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const currentCompany = useSelector(selectCompany(companyId));
  const companies = useSelector(availableCompanies);
  const dispatch = useDispatch();
  const filterInputRef = useRef(null);

  useEffect(() => {
    dispatch(companiesActions.syncCompanies());
  }, [dispatch]);

  const filteredCompanies = companies.filter((c) =>
    c.name.toLowerCase().includes(filter.toLowerCase()),
  );

  const openDropdown = () => {
    setFilter('');
    setFocusedIndex(-1);
    setDropdownOpen(true);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
    setFilter('');
    setFocusedIndex(-1);
  };

  const toggle = () => {
    if (dropdownOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  useEffect(() => {
    if (dropdownOpen && filterInputRef.current) {
      setTimeout(() => filterInputRef.current?.focus(), 50);
    }
  }, [dropdownOpen]);

  const handleCompanyChange = (newCompanyId) => {
    if (newCompanyId !== companyId) {
      dispatch(dashboardActions.changeCompany(newCompanyId));
    }
    closeDropdown();
  };

  const handleFilterKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, filteredCompanies.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const target =
          focusedIndex >= 0
            ? filteredCompanies[focusedIndex]
            : filteredCompanies[0];
        if (target) handleCompanyChange(target.id);
      } else if (e.key === 'Escape') {
        closeDropdown();
      }
    },
    [filteredCompanies, focusedIndex],
  );

  if (companies.length <= 1) {
    return (
      <div
        className="d-inline-block mr-2 mr-md-4 mini-stats-wid"
        style={shrinkableStyle}>
        <Media className="header-item">
          <div className="d-none d-sm-block align-self-center mr-2">
            <span className="avatar-xs rounded-circle">
              <i className="bx bxs-shapes font-size-22 text-info"></i>
            </span>
          </div>
          <div className="align-self-center overflow-hidden mr-3">
            <h5 className="font-size-15 mt-0 mb-1 text-truncate">
              {currentCompany ? currentCompany.name : ''}
            </h5>
          </div>
          {process.env.REACT_APP_ENVIRONMENT === 'dev' && (
            <div className="align-self-center mr-3">
              <span className="badge badge-danger font-size-14">DEV</span>
            </div>
          )}
        </Media>
      </div>
    );
  }

  return (
    <div className="d-inline-block mr-2 mr-md-4" style={shrinkableStyle}>
      <Dropdown isOpen={dropdownOpen} toggle={toggle}>
        <DropdownToggle
          tag="button"
          className="btn header-item mw-100 waves-effect"
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            padding: '0.5rem',
          }}>
          <Media className="align-items-center">
            <div className="d-none d-sm-block align-self-center mr-2">
              <span className="avatar-xs rounded-circle">
                <i className="bx bxs-shapes font-size-22 text-info"></i>
              </span>
            </div>
            <div className="align-self-center overflow-hidden mr-2">
              <h5 className="font-size-15 mt-0 mb-1 text-truncate">
                {currentCompany ? currentCompany.name : 'Select Company'}
              </h5>
            </div>
            <i className="mdi mdi-chevron-down"></i>
            {process.env.REACT_APP_ENVIRONMENT === 'dev' && (
              <div className="align-self-center ml-2">
                <span className="badge badge-danger font-size-14">DEV</span>
              </div>
            )}
          </Media>
        </DropdownToggle>
        <DropdownMenu
          right
          style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
          <div className="px-3 py-2" onKeyDown={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative' }}>
              <i
                className="bx bx-search"
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.secondary,
                  fontSize: '15px',
                  pointerEvents: 'none',
                }}
              />
              <input
                ref={filterInputRef}
                type="text"
                className="form-control form-control-sm"
                placeholder="Filter companies..."
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setFocusedIndex(-1);
                }}
                onKeyDown={handleFilterKeyDown}
                style={{ paddingLeft: '30px' }}
              />
            </div>
          </div>
          {filteredCompanies.length === 0 && (
            <DropdownItem disabled>No companies found</DropdownItem>
          )}
          {filteredCompanies.map((company, index) => (
            <DropdownItem
              key={company.id}
              onClick={() => handleCompanyChange(company.id)}
              active={company.id === companyId}
              style={
                index === focusedIndex
                  ? {
                      backgroundColor: colors.primary,
                      color: '#fff',
                      outline: 'none',
                    }
                  : undefined
              }>
              <i className="bx bxs-shapes font-size-16 align-middle mr-2"></i>
              {company.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default CompanySelector;
